import { useState } from "react";
import { EMERGENCY_TYPES, UNIT_COLOR, UNIT_SHORT } from "@/lib/gameConfig";

export default function CallQueue({ calls, stations, apparatusState, dispatches, onCallClick, onAutoDispatch, followUpQueue = {} }) {
  const [sortOrder, setSortOrder] = useState("oldest"); // 'oldest' | 'newest' | 'reward'
  const [filterMode, setFilterMode] = useState("all"); // 'all' | 'needs'

  // Per-call counts (used both for the "needs attention" filter and the unread badge counter).
  //
  // Queued Follow-Up units (busy on other calls, pre-assigned to
  // auto-dispatch here once free) count toward incoming coverage the
  // same way real en-route units do — so queuing a unit that satisfies
  // an outstanding requirement immediately removes the "!" bang and
  // drops the call out of the "Needs Attention" filter, matching the
  // dispatcher's mental model of "I've already sent everyone I need".
  const dispatchSummary = (c) => {
    let onScene = 0;
    let enRoute = 0;
    const incomingByType = {}; // on-scene + en-route + queued follow-ups per type
    dispatches.forEach((d) => {
      if (d.callId !== c.id || d.returning) return;
      const tp = d.apparatusType || "Engine";
      incomingByType[tp] = (incomingByType[tp] || 0) + 1;
      if (d.onScene) onScene += 1;
      else enRoute += 1;
    });
    const queuedIds = followUpQueue[String(c.id)] || [];
    queuedIds.forEach((aid) => {
      const tp = apparatusState?.[aid]?.type || "Engine";
      incomingByType[tp] = (incomingByType[tp] || 0) + 1;
    });
    const options = [c.requiredUnits || {}].concat(
      Array.isArray(c.alternates) ? c.alternates : []
    );
    // A call is "covered" if the combined on-scene + en-route + queued
    // fleet would satisfy at least one requirement option once everyone
    // arrives.
    const anyOptionCovered = options.some((opt) =>
      Object.entries(opt).every(([type, n]) => (incomingByType[type] || 0) >= n)
    );
    return { onScene, enRoute, anyOptionCovered };
  };

  // Patients with `assessed && needsTransport && transportStatus==="none"`
  // are waiting for the player to click Transport (and possibly waiting for
  // a Medic on scene to carry them). Drives the pink ! indicator and the
  // expanded "Needs Units" filter.
  const patientsAwaitingTransport = (c) =>
    (Array.isArray(c.patients) ? c.patients : []).filter(
      (p) => p.assessed && p.needsTransport && p.transportStatus === "none"
    ).length;

  // "Needs attention" = call is still pending AND either:
  //   - nothing en route + nothing on scene (no one dispatched), OR
  //   - revealed and no option is fully covered (on-scene + en-route combined), OR
  //   - at least one patient is awaiting transport (medic-side action needed)
  const needsAttention = (c) => {
    if (c.state !== "pending") return false;
    if (patientsAwaitingTransport(c) > 0) return true;
    const { onScene, enRoute, anyOptionCovered } = dispatchSummary(c);
    if (!c.revealed) return onScene + enRoute === 0;
    return !anyOptionCovered;
  };

  const totalNeedsAttention = calls.reduce(
    (n, c) => (needsAttention(c) ? n + 1 : n),
    0
  );

  const filtered = filterMode === "needs" ? calls.filter(needsAttention) : calls;
  const sorted = [...filtered].sort((a, b) => {
    if (a.state !== b.state) return a.state === "pending" ? -1 : 1;
    if (sortOrder === "reward") {
      const rA = EMERGENCY_TYPES[a.type]?.reward || 0;
      const rB = EMERGENCY_TYPES[b.type]?.reward || 0;
      if (rA !== rB) return rB - rA;
      return a.createdAt - b.createdAt;
    }
    return sortOrder === "oldest"
      ? a.createdAt - b.createdAt
      : b.createdAt - a.createdAt;
  });
  return (
    <aside className="absolute top-[60px] left-0 w-72 sm:w-80 max-w-[80vw] bottom-0 border-r border-white/10 bg-[#0F1115] z-40 flex flex-col">
      <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-b border-white/10 shrink-0">
        <h2 className="mono text-[11px] uppercase tracking-[0.25em] text-zinc-300 font-semibold">
          Incoming Calls
        </h2>
        <span className="mono text-[11px] text-red-400" data-testid="callqueue-count">
          {filterMode === "needs"
            ? `${sorted.length}/${calls.length} NEEDS`
            : `${calls.length} ACTIVE`}
        </span>
      </div>
      <div
        className="flex items-center gap-1 px-4 py-2 border-b border-white/10 shrink-0"
        data-testid="callqueue-sort-controls"
      >
        <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 mr-1">
          Sort
        </span>
        <button
          data-testid="callqueue-sort-oldest"
          onClick={() => setSortOrder("oldest")}
          className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 border transition-colors ${
            sortOrder === "oldest"
              ? "border-emerald-400 text-emerald-400 bg-emerald-400/10"
              : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
          }`}
        >
          Oldest ↑
        </button>
        <button
          data-testid="callqueue-sort-newest"
          onClick={() => setSortOrder("newest")}
          className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 border transition-colors ${
            sortOrder === "newest"
              ? "border-emerald-400 text-emerald-400 bg-emerald-400/10"
              : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
          }`}
        >
          Newest ↑
        </button>
        <button
          data-testid="callqueue-sort-reward"
          onClick={() => setSortOrder("reward")}
          className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 border transition-colors ${
            sortOrder === "reward"
              ? "border-emerald-400 text-emerald-400 bg-emerald-400/10"
              : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
          }`}
          title="Highest reward at top"
        >
          $ ↑
        </button>
      </div>
      <div
        className="flex items-center gap-1 px-4 py-2 border-b border-white/10 shrink-0"
        data-testid="callqueue-filter-controls"
      >
        <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 mr-1">
          Filter
        </span>
        <button
          data-testid="callqueue-filter-all"
          onClick={() => setFilterMode("all")}
          className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 border transition-colors ${
            filterMode === "all"
              ? "border-emerald-400 text-emerald-400 bg-emerald-400/10"
              : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
          }`}
        >
          All
        </button>
        <button
          data-testid="callqueue-filter-needs"
          onClick={() => setFilterMode("needs")}
          title="Show calls missing required units OR with patients awaiting medic transport"
          className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 border transition-colors flex items-center gap-1 ${
            filterMode === "needs"
              ? "border-red-500/70 text-red-300 bg-red-500/10"
              : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
          }`}
        >
          ! Needs Units
          {totalNeedsAttention > 0 && (
            <span
              data-testid="callqueue-filter-needs-count"
              className={`mono text-[9px] tabular-nums px-1 ${
                filterMode === "needs" ? "bg-red-500/30 text-red-200" : "bg-white/10 text-zinc-300"
              }`}
            >
              {totalNeedsAttention}
            </span>
          )}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto" data-testid="call-list">
        {sorted.length === 0 && (
          <div className="p-6 text-center mono text-[11px] text-zinc-600 uppercase tracking-widest">
            {filterMode === "needs" && calls.length > 0
              ? "— all calls covered —"
              : "— quiet on the line —"}
          </div>
        )}
        {sorted.map((c) => (
          <CallCard
            key={c.id}
            call={c}
            stations={stations}
            apparatusState={apparatusState}
            dispatches={dispatches}
            onCallClick={onCallClick}
            onAutoDispatch={onAutoDispatch}
            followUpQueue={followUpQueue}
          />
        ))}
      </div>
    </aside>
  );
}

function CallCard({ call, stations, apparatusState, dispatches, onCallClick, onAutoDispatch, followUpQueue = {} }) {
  const t = EMERGENCY_TYPES[call.type];
  const now = Date.now();
  // Defensive: if the saved call references a retired emergency type
  // (e.g., older `medical` / `hazmat` rows from before a CSV swap), the
  // type lookup returns undefined. Skip rendering the card instead of
  // crashing the whole CallQueue (and therefore the entire game UI).
  if (!t) return null;

  // Per-type on-scene
  const onScene = {};
  const enRoute = {};
  dispatches.forEach((d) => {
    if (d.callId !== call.id || d.returning) return;
    const tp = d.apparatusType || "Engine";
    if (d.onScene) onScene[tp] = (onScene[tp] || 0) + 1;
    else enRoute[tp] = (enRoute[tp] || 0) + 1;
  });
  // Queued Follow-Up units per type (busy on other calls, pre-assigned
  // to auto-dispatch here). Not summed into enRoute here — we track them
  // separately so the "En Route" count remains accurate for the current
  // fleet in motion, but they DO fold into `incomingByType` below so the
  // "needs more units" bang clears the instant a follow-up is queued.
  const queued = {};
  const queuedIds = followUpQueue[String(call.id)] || [];
  queuedIds.forEach((aid) => {
    const tp = apparatusState?.[aid]?.type || "Engine";
    queued[tp] = (queued[tp] || 0) + 1;
  });
  const requirementOptions = [call.requiredUnits].concat(
    Array.isArray(call.alternates) ? call.alternates : []
  );
  // For "total needed" / progress, pick the cheapest (least total units) option.
  const optionSum = (opt) => Object.values(opt).reduce((a, b) => a + b, 0);
  const cheapestOption = [...requirementOptions].sort((a, b) => optionSum(a) - optionSum(b))[0] || {};
  const optionMet = (opt) =>
    Object.entries(opt).every(([type, n]) => (onScene[type] || 0) >= n);
  const anyOptionMet = requirementOptions.some(optionMet);
  // Combined on-scene + en-route + queued-follow-up counts per type —
  // used to decide if the call is fully covered (no extra dispatch
  // needed). Queued units count here because they will auto-dispatch as
  // soon as they clear their current call.
  const incomingByType = {};
  Object.entries(onScene).forEach(([k, v]) => { incomingByType[k] = (incomingByType[k] || 0) + v; });
  Object.entries(enRoute).forEach(([k, v]) => { incomingByType[k] = (incomingByType[k] || 0) + v; });
  Object.entries(queued).forEach(([k, v]) => { incomingByType[k] = (incomingByType[k] || 0) + v; });
  const anyOptionCovered = requirementOptions.some((opt) =>
    Object.entries(opt).every(([type, n]) => (incomingByType[type] || 0) >= n)
  );
  const totalNeeded = optionSum(cheapestOption);
  const totalOnScene = Object.values(onScene).reduce((a, b) => a + b, 0);
  const totalEnRoute = Object.values(enRoute).reduce((a, b) => a + b, 0);

  const hasAvail = stations.some((s) =>
    (s.trucks || []).some((tr) => apparatusState[tr.id]?.available)
  );

  // Red exclamation: requirements revealed, still pending, AND no requirement
  // option is fully covered by on-scene + en-route units combined.
  const needsMoreUnits =
    call.revealed &&
    call.state === "pending" &&
    !anyOptionCovered;

  // Pink exclamation: patients awaiting transport assignment. Fires in two
  // situations the user asked us to surface:
  //   (1) Not enough on-scene Medics to carry every patient → user must
  //       dispatch additional Medics.
  //   (2) Medics ARE on scene but the player hasn't clicked Transport yet
  //       to pick a destination hospital.
  const patientsAwaitingTransport = (Array.isArray(call.patients) ? call.patients : []).filter(
    (p) => p.assessed && p.needsTransport && p.transportStatus === "none"
  ).length;
  const onSceneFreeMedics = dispatches.filter(
    (d) => {
      if (d.callId !== call.id || !d.onScene || d.transporting || d.returning) return false;
      const t = d.apparatusType || "Engine";
      return t === "Medic" || t === "HEMS";  // HEMS can transport too
    }
  ).length;
  const pinkNeedsTransport = patientsAwaitingTransport > 0;
  const pinkNeedsMoreMedics = patientsAwaitingTransport > onSceneFreeMedics;
  const pinkTitle = pinkNeedsMoreMedics
    ? `${patientsAwaitingTransport} patient${patientsAwaitingTransport > 1 ? "s" : ""} awaiting transport — not enough Medics on scene (${onSceneFreeMedics} free)`
    : `${patientsAwaitingTransport} patient${patientsAwaitingTransport > 1 ? "s" : ""} ready — click Transport to pick a hospital`;

  // Timer: ON SCENE shows remaining work; PENDING shows "WAITING"
  let timerLabel = "—";
  let timerSub = "";
  let pct = 0;
  let timerColor = t.color;
  if (call.state === "on_scene") {
    const elapsed = (now - call.onSceneStartedAt) / 1000;
    const remaining = Math.max(0, Math.ceil(call.onSceneDurationSec - elapsed));
    timerLabel = `${remaining}s`;
    timerSub = `OF ${call.onSceneDurationSec}s`;
    pct = Math.max(0, Math.min(100, 100 - (elapsed / call.onSceneDurationSec) * 100));
    timerColor = "#30D158";
  } else if (call.revealed) {
    timerLabel = `${totalOnScene}/${totalNeeded}`;
    timerSub = `NEEDS ${call.onSceneDurationSec}s ONCE READY`;
    pct = totalNeeded > 0 ? (totalOnScene / totalNeeded) * 100 : 0;
    timerColor = "#FFD60A";
  } else {
    timerLabel = "???";
    timerSub = totalEnRoute > 0 ? "INVESTIGATING" : "AWAITING UNIT";
    pct = 0;
    timerColor = "#9CA3AF";
  }

  return (
    <div
      data-testid={`call-${call.id}`}
      onClick={() => onCallClick && onCallClick(call)}
      className="border-b border-white/5 px-2 py-1.5 relative cursor-pointer hover:bg-white/[0.03] transition-colors leading-tight"
      style={{ borderLeft: `4px solid ${t.color}` }}
    >
      {/* Row 1: call label · state pill · units-needed bang · live timer */}
      <div className="flex items-center gap-1.5">
        <span className="text-[13px] text-white font-semibold truncate" title={t.label}>
          {t.label}
        </span>
        <span
          className={`mono text-[9px] font-bold uppercase tracking-widest px-1 py-px border shrink-0 ${
            call.state === "on_scene"
              ? "border-emerald-500 text-emerald-400"
              : "border-yellow-500 text-yellow-400"
          }`}
        >
          {call.state === "on_scene" ? "ON" : "PEND"}
        </span>
        {needsMoreUnits && (
          <span
            data-testid={`call-needs-more-${call.id}`}
            title="More units needed — none currently responding"
            className="mono text-[10px] font-bold text-white bg-red-600 border border-red-400 px-1 py-px animate-pulse leading-none shrink-0"
            style={{ boxShadow: "0 0 8px rgba(239,68,68,0.7)" }}
          >
            !
          </span>
        )}
        {pinkNeedsTransport && (
          <span
            data-testid={`call-needs-transport-${call.id}`}
            title={pinkTitle}
            className="mono text-[10px] font-bold text-white bg-pink-500 border border-pink-300 px-1 py-px animate-pulse leading-none shrink-0"
            style={{ boxShadow: "0 0 8px rgba(236,72,153,0.7)" }}
          >
            !
          </span>
        )}
        <span
          className={`ml-auto mono text-[13px] font-bold shrink-0 ${
            call.state === "on_scene"
              ? "text-emerald-400"
              : call.revealed
              ? "text-yellow-400"
              : "text-zinc-400"
          }`}
          data-testid={`call-timer-${call.id}`}
          title={timerSub}
        >
          {timerLabel}
        </span>
      </div>

      {/* Row 2: full address. Allows up to 2 wrapped lines via line-clamp
          so short addresses sit on one row and very long ones don't bloat
          the card past a reasonable height. */}
      <div
        className="mono text-[10px] text-zinc-400 uppercase mt-0.5 break-words"
        style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
        title={call.address}
      >
        {call.address}
      </div>

      {/* Row 3: requirements (or INVESTIGATE stub) · progress · reward · +1 */}
      <div className="flex items-center gap-1.5 mt-1">
        {call.revealed ? (
          <div className="flex flex-wrap items-center gap-0.5 shrink-0" data-testid={`req-chips-${call.id}`}>
            {requirementOptions.map((opt, optIdx) => (
              <span key={optIdx} className="flex items-center gap-0.5">
                {optIdx > 0 && (
                  <span className="mono text-[8px] uppercase tracking-widest text-zinc-500 px-0.5">
                    OR
                  </span>
                )}
                {Object.entries(opt).map(([type, n]) => {
                  const have = onScene[type] || 0;
                  const ok = have >= n;
                  return (
                    <span
                      key={`${optIdx}-${type}`}
                      className={`mono text-[8px] uppercase tracking-widest px-1 border ${
                        ok ? "border-emerald-500/60 text-emerald-400" : "border-yellow-500/40 text-yellow-400"
                      }`}
                      style={{ borderLeft: `2px solid ${UNIT_COLOR[type]}` }}
                      title={type}
                    >
                      {UNIT_SHORT[type]} {have}/{n}
                    </span>
                  );
                })}
              </span>
            ))}
            {/* Patients-still-needing-a-Medic indicator. We count patients
                whose transportStatus is "none" (unassessed OR assessed and
                awaiting transport). Refused / in_transit / delivered patients
                no longer need a Medic and don't bump the chip. */}
            {(() => {
              const pts = Array.isArray(call.patients) ? call.patients : [];
              const need = pts.filter((p) => p.transportStatus === "none").length;
              if (need === 0) return null;
              return (
                <span
                  className="mono text-[8px] uppercase tracking-widest px-1 border border-pink-500/60 text-pink-300"
                  style={{ borderLeft: `2px solid ${UNIT_COLOR["Medic"]}` }}
                  title={`${need} patient${need > 1 ? "s" : ""} on scene needing a Medic for transport`}
                  data-testid={`med-needed-${call.id}`}
                >
                  {UNIT_SHORT["Medic"]} {need}
                </span>
              );
            })()}
          </div>
        ) : (
          <span
            className="mono text-[8px] uppercase tracking-widest px-1 border border-zinc-600 text-zinc-400 shrink-0"
            data-testid={`req-hidden-${call.id}`}
          >
            INVESTIGATE
          </span>
        )}
        <div className="h-1 bg-white/5 flex-1 overflow-hidden min-w-[40px]">
          <div
            className="h-full transition-[width] duration-1000 ease-linear"
            style={{ width: `${pct}%`, background: timerColor }}
          />
        </div>
        <span className="mono text-[10px] text-emerald-400 shrink-0">+${t.reward.toLocaleString()}</span>
        {call.state === "pending" && hasAvail && (
          <button
            data-testid={`dispatch-btn-${call.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onAutoDispatch && onAutoDispatch(call.id);
            }}
            className="mono text-[10px] uppercase tracking-widest text-black bg-emerald-400 hover:bg-emerald-300 px-1.5 py-px shrink-0"
          >
            +1
          </button>
        )}
      </div>
    </div>
  );
}
